/**
 * Public /employment-coaching — the paid Employment Preparation & Coaching
 * Package ($499 AUD one-off). Client enters name + email, agrees to the
 * coaching terms → we open a $499 Stripe checkout → Stripe redirects to
 * /employment-coaching/confirmation. Kept on-brand with the rest of the site.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, Lock, Plane } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICE_AUD = 499;

const INCLUSIONS = [
  "Professional CV rewrite tailored to the Australian job market",
  "LinkedIn profile optimisation",
  "60-minute one-to-one career strategy session",
  "Interview coaching and mock interview preparation",
  "Australian job-market and salary guidance",
  "Personalised job-search strategy",
  "ATS and application guidance",
  "Weekly check-ins",
  "Feedback on suitable roles and applications",
  "Salary and offer negotiation guidance",
  "30 days of one-to-one support",
];

const CONSENT_TEXT =
  "I understand that this package provides employment preparation, coaching and job-search support. It does not guarantee interviews, job placement or an offer of employment.";

export default function EmploymentCoaching() {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.first_name) { toast.error("First name and email are required."); return; }
    if (!consent) { toast.error("Please accept the coaching terms to continue."); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/coaching/checkout`, {
        email: form.email.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        origin_url: window.location.origin,
        consent_accepted: consent,
      });
      if (!r.data?.url) throw new Error("No checkout URL returned.");
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — email us at hello@theditchcollective.co.nz");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="coaching-page">
      <header className="px-5 md:px-8 py-5 border-b border-[var(--line)]">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
      </header>

      <main className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14 grid md:grid-cols-2 gap-10 md:gap-14 items-start">
        {/* Left — the pitch */}
        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[var(--ink)] font-bold bg-[var(--lime)] px-3 py-1.5 rounded-full">
            Employment Preparation & Coaching
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[var(--ink)] mt-5 leading-[1.08]">
            Get employment-ready for Australia with 30 days of one-to-one career support.
          </h1>
          <p className="text-[var(--ocean)] mt-5 text-base leading-relaxed">
            Designed for people preparing to enter the Australian job market, this package gives you
            personalised support with your CV, LinkedIn profile, job-search strategy and interview
            preparation.
          </p>

          <h2 className="font-display text-lg font-bold text-[var(--ink)] mt-8 mb-3">What's included</h2>
          <ul className="space-y-2.5">
            {INCLUSIONS.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[15px] text-[var(--ink)]" data-testid={`coaching-inclusion-${i}`}>
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[var(--lime)] flex items-center justify-center">
                  <Check size={12} className="text-[var(--ink)]" />
                </span>
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-2xl border border-[var(--line)] bg-white/60 p-5 text-[13px] text-[var(--ocean)] leading-relaxed">
            <b className="text-[var(--ink)]">Important:</b> This is a paid coaching and employment-preparation
            service provided through The Ditch Collective. Purchasing this package does not give you priority
            access to vacancies and does not guarantee recruitment representation, interviews or employment.
            You may separately register free of charge with{" "}
            <a href="https://www.theditchcareers.com.au/register" target="_blank" rel="noreferrer" className="underline text-[var(--ink)]">
              The Ditch Careers
            </a>{" "}
            to be considered for genuine vacancies.
          </div>
        </div>

        {/* Right — the checkout card */}
        <div className="md:sticky md:top-8">
          <form onSubmit={submit} className="bg-white rounded-2xl border border-[var(--line)] p-7 shadow-sm" data-testid="coaching-form">
            <div className="flex items-baseline justify-between pb-4 border-b border-[var(--line)]">
              <span className="text-xs uppercase tracking-widest font-bold text-[var(--ocean)]">One-off payment</span>
              <span className="font-display text-4xl font-extrabold text-[var(--ink)]">${PRICE_AUD}<span className="text-sm font-normal text-[var(--ocean)] ml-1">AUD</span></span>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-1">First name <span className="text-red-500">*</span></span>
                <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="coaching-first-name" placeholder="Alex"
                  className="w-full rounded-lg border border-[var(--line)] px-3 py-2.5 text-[15px] focus:border-[var(--ink)] outline-none" />
              </label>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-1">Last name</span>
                <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="coaching-last-name" placeholder="Smith"
                  className="w-full rounded-lg border border-[var(--line)] px-3 py-2.5 text-[15px] focus:border-[var(--ink)] outline-none" />
              </label>
            </div>
            <label className="block mt-3">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-1">Email <span className="text-red-500">*</span></span>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="coaching-email" placeholder="alex@example.com"
                className="w-full rounded-lg border border-[var(--line)] px-3 py-2.5 text-[15px] focus:border-[var(--ink)] outline-none" />
            </label>

            <label className="flex items-start gap-2.5 mt-5 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                data-testid="coaching-consent"
                className="mt-0.5 w-4 h-4 rounded shrink-0 cursor-pointer" />
              <span className="text-[13px] text-[var(--ink)] leading-relaxed">{CONSENT_TEXT}</span>
            </label>

            <button type="submit" disabled={submitting || !form.email || !form.first_name || !consent}
              data-testid="coaching-submit"
              className="w-full mt-6 inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[var(--ink)] text-[var(--paper)] font-display font-bold hover:brightness-110 transition-all disabled:opacity-50">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <>Get Started <ArrowRight size={16} /></>}
            </button>
            <p className="mt-3 text-[11px] text-[var(--ocean)] text-center inline-flex items-center gap-1.5 justify-center w-full">
              <Lock size={11} /> Secure checkout via Stripe · $499 AUD, one-off payment
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
